import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { proveedoresAPI } from '../services/api';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';

const Proveedores = () => {
  const { refreshCatalogos, formatCurrency } = useApp();
  const { modoEspectador } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editando, setEditando] = useState(null);
  const [filtros, setFiltros] = useState({ buscar: '', ordenar: 'nombre' });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [formData, setFormData] = useState({ 
    codigo: '', nombre: '', ruc: '', direccion: '', telefono: '', 
    email: '', contacto_nombre: '', contacto_telefono: '', notas: '' 
  });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = { limit: 20, page };
      if (filtros.buscar) params.buscar = filtros.buscar;
      const res = await proveedoresAPI.getAll(params);
      if (res.data.success) {
        let data = res.data.data;
        if (filtros.ordenar === 'compras') {
          data.sort((a, b) => (b.total_compras || 0) - (a.total_compras || 0));
        } else if (filtros.ordenar === 'monto') {
          data.sort((a, b) => (b.monto_total || 0) - (a.monto_total || 0));
        }
        setItems(data);
        setTotal(res.data.total || data.length);
        setTotalPages(res.data.totalPages || 1);
      }
    }
    catch (e) { toast.error('Error al cargar'); }
    finally { setLoading(false); }
  }, [filtros, page]);

  useEffect(() => { load(); }, [load]);

  const handleFiltroChange = (e) => {
    const { name, value } = e.target;
    setPage(1);
    setFiltros(prev => ({ ...prev, [name]: value }));
  };

  const limpiarFiltros = () => { setFiltros({ buscar: '', ordenar: 'nombre' }); setPage(1); };

  const openModal = (item = null) => {
    if (item) { 
      setEditando(item); 
      setFormData({ 
        codigo: item.codigo || '',
        nombre: item.nombre || '',
        ruc: item.ruc || '',
        direccion: item.direccion || '',
        telefono: item.telefono || '',
        email: item.email || '',
        contacto_nombre: item.contacto_nombre || '',
        contacto_telefono: item.contacto_telefono || '',
        notas: item.notas || ''
      }); 
    }
    else { 
      setEditando(null); 
      setFormData({ 
        codigo: '', nombre: '', ruc: '', direccion: '', telefono: '', 
        email: '', contacto_nombre: '', contacto_telefono: '', notas: '' 
      }); 
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nombre) { toast.error('El nombre es requerido'); return; }
    try {
      if (editando) { 
        await proveedoresAPI.update(editando.id, formData); 
        toast.success('Proveedor actualizado'); 
      }
      else { 
        await proveedoresAPI.create(formData); 
        toast.success('Proveedor creado'); 
      }
      setShowModal(false); 
      load(); 
      refreshCatalogos();
    } catch (e) { toast.error(e.response?.data?.error || 'Error al guardar'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este proveedor?')) return;
    try { 
      await proveedoresAPI.delete(id); 
      toast.success('Proveedor eliminado'); 
      load(); 
      refreshCatalogos(); 
    }
    catch (e) { toast.error('Error al eliminar'); }
  };

  const hayFiltros = filtros.buscar;

  return (
    <div>
      {/* Filtros */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row align-items-end g-3">
            <div className="col-md-5">
              <label className="form-label">Buscar</label>
              <div className="input-group">
                <span className="input-group-text"><i className="bi bi-search"></i></span>
                <input 
                  type="text" 
                  className="form-control" 
                  placeholder="Nombre, código, RUC..." 
                  name="buscar"
                  value={filtros.buscar} 
                  onChange={handleFiltroChange} 
                />
              </div>
            </div>
            <div className="col-md-3">
              <label className="form-label">Ordenar por</label>
              <select className="form-select" name="ordenar" value={filtros.ordenar} onChange={handleFiltroChange}>
                <option value="nombre">Nombre A-Z</option>
                <option value="compras">Mayor N° Compras</option>
                <option value="monto">Mayor Monto</option>
              </select>
            </div>
            <div className="col-md-4">
              <div className="d-flex gap-2">
                {hayFiltros && (
                  <button className="btn btn-outline-secondary" onClick={limpiarFiltros} title="Limpiar filtros">
                    <i className="bi bi-x-lg"></i>
                  </button>
                )}
                <button className="btn btn-primary flex-grow-1" onClick={() => openModal()} disabled={modoEspectador} title={modoEspectador ? 'No disponible en modo espectador' : ''}>
                  <i className="bi bi-plus-lg me-2"></i>Nuevo Proveedor
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabla */}
      <div className="card">
        <div className="card-body p-0">
          {loading ? (
            <div className="loading-container"><div className="spinner-border text-primary"></div></div>
          ) : items.length === 0 ? (
            <div className="empty-state py-5">
              <i className="bi bi-truck"></i>
              <h5>No hay proveedores{hayFiltros ? ' con los filtros seleccionados' : ''}</h5>
              {hayFiltros && <button className="btn btn-outline-primary mt-3" onClick={limpiarFiltros}>Limpiar Filtros</button>}
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead>
                  <tr>
                    <th>Código</th>
                    <th>Proveedor</th>
                    <th>RUC</th>
                    <th>Contacto</th>
                    <th>Email / Teléfono</th>
                    <th className="text-center">Compras</th>
                    <th className="text-end">Total Comprado</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(item => (
                    <tr key={item.id}>
                      <td><code>{item.codigo}</code></td>
                      <td>
                        <div className="fw-medium">{item.nombre}</div>
                        {item.direccion && <small className="text-muted d-block">{item.direccion}</small>}
                      </td>
                      <td>{item.ruc || <span className="text-muted">-</span>}</td>
                      <td>{item.contacto_nombre || <span className="text-muted">-</span>}</td>
                      <td>
                        {item.email && <div><i className="bi bi-envelope me-1 text-muted"></i>{item.email}</div>}
                        {item.telefono && <div><i className="bi bi-telephone me-1 text-muted"></i>{item.telefono}</div>}
                        {!item.email && !item.telefono && <span className="text-muted">-</span>}
                      </td>
                      <td className="text-center">
                        <span className="badge bg-primary">{item.total_compras || 0}</span>
                      </td>
                      <td className="text-end fw-bold">{formatCurrency(item.monto_total || 0)}</td>
                      <td>
                        <div className="btn-group btn-group-sm">
                          <button className="btn btn-outline-primary" onClick={() => openModal(item)} title="Editar" disabled={modoEspectador}>
                            <i className="bi bi-pencil"></i>
                          </button>
                          <button className="btn btn-outline-danger" onClick={() => handleDelete(item.id)} title="Eliminar" disabled={modoEspectador}>
                            <i className="bi bi-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        {items.length > 0 && (
          <div className="card-footer d-flex justify-content-between align-items-center">
            <span className="text-muted">Mostrando {items.length} de {total} proveedor(es)</span>
            {totalPages > 1 && (
              <nav>
                <ul className="pagination pagination-sm mb-0">
                  <li className={`page-item ${page <= 1 ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => setPage(p => p - 1)}>Anterior</button>
                  </li>
                  {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                    let pg;
                    if (totalPages <= 5) pg = i + 1;
                    else if (page <= 3) pg = i + 1;
                    else if (page >= totalPages - 2) pg = totalPages - 4 + i;
                    else pg = page - 2 + i;
                    return (
                      <li key={pg} className={`page-item ${page === pg ? 'active' : ''}`}>
                        <button className="page-link" onClick={() => setPage(pg)}>{pg}</button>
                      </li>
                    );
                  })}
                  <li className={`page-item ${page >= totalPages ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => setPage(p => p + 1)}>Siguiente</button>
                  </li>
                </ul>
              </nav>
            )}
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className={`bi ${editando ? 'bi-pencil' : 'bi-plus-circle'} me-2`}></i>
                  {editando ? 'Editar' : 'Nuevo'} Proveedor
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="row g-3">
                    <div className="col-md-4">
                      <label className="form-label">Código</label>
                      <input type="text" className="form-control" value={formData.codigo} onChange={e => setFormData({...formData, codigo: e.target.value})} placeholder="Auto-generado" />
                    </div>
                    <div className="col-md-8">
                      <label className="form-label">Nombre / Razón Social *</label>
                      <input type="text" className="form-control" value={formData.nombre} onChange={e => setFormData({...formData, nombre: e.target.value})} required />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">RUC</label>
                      <input type="text" className="form-control" value={formData.ruc} onChange={e => setFormData({...formData, ruc: e.target.value})} />
                    </div>
                    <div className="col-md-8">
                      <label className="form-label">Dirección</label>
                      <input type="text" className="form-control" value={formData.direccion} onChange={e => setFormData({...formData, direccion: e.target.value})} />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Teléfono</label>
                      <input type="text" className="form-control" value={formData.telefono} onChange={e => setFormData({...formData, telefono: e.target.value})} />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Email</label>
                      <input type="email" className="form-control" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} />
                    </div>
                    
                    <div className="col-12"><hr className="my-2" /><h6 className="text-muted">Persona de Contacto</h6></div>
                    
                    <div className="col-md-6">
                      <label className="form-label">Nombre del Contacto</label>
                      <input type="text" className="form-control" value={formData.contacto_nombre} onChange={e => setFormData({...formData, contacto_nombre: e.target.value})} />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Teléfono del Contacto</label>
                      <input type="text" className="form-control" value={formData.contacto_telefono} onChange={e => setFormData({...formData, contacto_telefono: e.target.value})} />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Notas</label>
                      <textarea className="form-control" value={formData.notas} onChange={e => setFormData({...formData, notas: e.target.value})} rows="2" placeholder="Observaciones adicionales..." />
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancelar</button>
                  <button type="submit" className="btn btn-primary">
                    <i className="bi bi-check-lg me-2"></i>{editando ? 'Actualizar' : 'Guardar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Proveedores;
